import type { ReadonlyDate } from '.'

export const isReadonlyDate = (date: unknown): date is ReadonlyDate => date instanceof Date

/**
 * Formats a date's time as HH:mm.
 */
export const formatTime = (date: ReadonlyDate): string => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`

/**
 * Formats a date's day/month/year as dd/MM/yyyy, matching the fixed pattern used elsewhere for
 * dates (e.g. the "DATETIME" translation's `dateRangeChip` format) rather than a locale-dependent one.
 */
export const formatDate = (date: ReadonlyDate): string =>
    `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`

/**
 * Used to validate start date doesn't exceed end date
 */
export const isDateAfter = (startDate: ReadonlyDate | null, endDate: ReadonlyDate | null): boolean =>
    startDate !== null && endDate !== null && startDate.getTime() > endDate.getTime()

/**
 * True when both dates fall on the same year/month/day, ignoring time-of-day.
 */
export const isSameCalendarDay = (a: ReadonlyDate, b: ReadonlyDate): boolean =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/

/**
 * The inverse of {@link formatTime}: merges an HH:mm time string onto `date`'s year/month/day, or
 * returns `undefined` if `time` isn't a valid HH:mm.
 */
export const mergeDateTime = (date: ReadonlyDate, time: string): Date | undefined => {
    const match = TIME_PATTERN.exec(time)
    if (!match) return undefined

    const result = new Date(date.getTime())
    result.setHours(Number(match[1]), Number(match[2]), 0, 0)
    return result
}

/**
 * The last millisecond of `date`'s minute, so an HH:mm upper bound includes everything logged during that minute.
 */
export const toEndOfMinute = (date: ReadonlyDate): Date => {
    const result = new Date(date.getTime())
    result.setSeconds(59, 999)
    return result
}
